import { Button, Text } from "@medusajs/ui"
import { useParams, useSearchParams } from "react-router-dom"
import { ErrorState } from "@/components/ui"
import { formatDate, formatMoney } from "@/lib/format"
import { useOrderDetail } from "@/lib/orders"
import { useOrderName } from "@/lib/permissions"
import { useGeneralSettings } from "@/lib/settings"
import { formatAddressLines } from "@/lib/geo"

const addr = (a: any) => formatAddressLines(a)

/** Printable packing slip (no prices) or invoice. Opens in its own tab; the browser's Print/Save as PDF does the rest. */
export function OrderPrintPage() {
  const { id = "" } = useParams()
  const [params] = useSearchParams()
  const invoice = params.get("type") === "invoice"
  const orderName = useOrderName()
  const { data, error } = useOrderDetail(id)
  const general = useGeneralSettings().data?.settings
  const o: any = data?.order
  if (error) return <ErrorState error={error} />
  if (!o) return <Text size="small" className="p-6">Loading…</Text>
  const cur = o.currency_code
  const money = (n: unknown) => formatMoney(Number(n ?? 0), cur)
  const from = [general?.legal_name || general?.store_name, ...formatAddressLines(general?.address, { name: false, phone: false }), general?.store_email].filter(Boolean) as string[]
  return (
    <div data-store-document className="mx-auto max-w-3xl bg-white p-8 text-[13px] text-black print:p-0">
      <style>{`body { background: #fff !important } @media print { @page { margin: 14mm } body { background: #fff !important } .no-print { display: none !important } }`}</style>
      <div className="no-print mb-6 flex items-center justify-between rounded-lg bg-ui-bg-subtle p-3">
        <Text size="small">{invoice ? "Invoice" : "Packing slip"} for {orderName(o.display_id)}</Text>
        <Button size="small" onClick={() => window.print()}>Print</Button>
      </div>
      <div className="flex items-start justify-between gap-6">
        <div>
          <h1 className="text-[22px] font-bold">{invoice ? "Invoice" : "Packing slip"}</h1>
          <div className="mt-1">Order {orderName(o.display_id)}</div>
          <div>{formatDate(o.created_at)}</div>
        </div>
        <div className="text-right leading-5">{from.map((l, i) => <div key={i} className={i === 0 ? "font-semibold" : ""}>{l}</div>)}</div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-6">
        <div><div className="mb-1 font-semibold">Ship to</div>{addr(o.shipping_address).map((l, i) => <div key={i}>{l}</div>)}</div>
        {invoice && <div><div className="mb-1 font-semibold">Bill to</div>{addr(o.billing_address ?? o.shipping_address).map((l, i) => <div key={i}>{l}</div>)}</div>}
      </div>
      <table className="mt-6 w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-black text-left"><th className="py-2">Item</th><th className="py-2 text-right">Qty</th>{invoice && <><th className="py-2 text-right">Price</th><th className="py-2 text-right">Total</th></>}</tr>
        </thead>
        <tbody>
          {(o.items ?? []).map((i: any) => (
            <tr key={i.id} className="border-b border-gray-300 align-top">
              <td className="py-2">{i.title}{i.subtitle && i.subtitle !== i.title && i.subtitle !== "Default Title" ? <div className="text-gray-600">{i.subtitle}</div> : null}{i.variant_sku ? <div className="text-gray-600">SKU {i.variant_sku}</div> : null}</td>
              <td className="py-2 text-right">{i.quantity}</td>
              {invoice && <><td className="py-2 text-right">{money(i.unit_price)}</td><td className="py-2 text-right">{money(Number(i.unit_price) * Number(i.quantity))}</td></>}
            </tr>
          ))}
        </tbody>
      </table>
      {invoice ? (
        <div className="ml-auto mt-4 w-64 space-y-1">
          <Row label="Subtotal" v={money(o.item_total ?? o.subtotal)} />
          {Number(o.discount_total) > 0 && <Row label="Discount" v={`-${money(o.discount_total)}`} />}
          <Row label="Shipping" v={money(o.shipping_total)} />
          <Row label="Taxes" v={money(o.tax_total)} />
          <div className="border-t-2 border-black pt-1"><Row label="Total" v={money(o.original_total ?? o.total)} bold /></div>
        </div>
      ) : (
        <div className="mt-6 text-gray-700">Shipping method: {o.shipping_methods?.[0]?.name ?? "—"}</div>
      )}
      <div className="mt-10 text-center text-gray-600">Thank you for your order!</div>
    </div>
  )
}

const Row = ({ label, v, bold }: { label: string; v: string; bold?: boolean }) => (
  <div className={`flex justify-between ${bold ? "font-bold" : ""}`}><span>{label}</span><span>{v}</span></div>
)
